import React, { useEffect, useCallback } from "react";
import "./styles/CitySelector.scss";

interface City {
  id: string;
  name: string;
  address?: string;
}

interface CitySelectorProps {
  selectedCity: City | null;
  onCityChange: (city: City) => void;
  availableCities: City[];
  isLoading?: boolean;
}

const formatCityLabel = (city: City): string => {
  if (!city.address?.trim()) return city.name;
  return `${city.name}, ${city.address}`;
};

export const CitySelector: React.FC<CitySelectorProps> = ({
  selectedCity,
  onCityChange,
  availableCities,
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        isOpen &&
        !target.closest(".city-selector__dropdown") &&
        !target.closest(".city-selector__button")
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeDropdown, isOpen]);

  if (isLoading) {
    return (
      <div className="city-selector">
        <button className="city-selector__button" disabled>
          <span className="city-selector__loading">Загрузка...</span>
        </button>
      </div>
    );
  }

  if (!selectedCity) {
    return null;
  }

  return (
    <>
      <div className="city-selector">
        <button
          className="city-selector__button"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className="city-selector__icon">📍</span>
          <span className="city-selector__name">{selectedCity.name}</span>
          <span
            className={`city-selector__arrow ${
              isOpen ? "city-selector__arrow--open" : ""
            }`}
          >
            ▼
          </span>
        </button>
      </div>

      {isOpen && (
        <>
          <div
            className="city-selector__overlay"
            onClick={closeDropdown}
            aria-hidden="true"
          />
          <div
            className="city-selector__dropdown"
            role="dialog"
            aria-label="Выберите город"
          >
            <div className="city-selector__header">
              <div className="city-selector__header-title">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                  <circle
                    cx="12"
                    cy="9"
                    r="3"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                </svg>
                <span>Выберите город</span>
              </div>
              <button
                type="button"
                className="city-selector__close"
                onClick={closeDropdown}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div
              className="city-selector__list"
              role="listbox"
              aria-label="Список городов"
            >
              {availableCities.map((city) => (
                <button
                  key={city.id}
                  type="button"
                  role="option"
                  aria-selected={selectedCity.id === city.id}
                  className={`city-selector__item ${
                    selectedCity.id === city.id
                      ? "city-selector__item--selected"
                      : ""
                  }`}
                  onClick={() => {
                    onCityChange(city);
                    closeDropdown();
                  }}
                >
                  <span className="city-selector__item-label">
                    {formatCityLabel(city)}
                  </span>
                </button>
              ))}
            </div>

            <div className="city-selector__footer">
              <button
                type="button"
                className="city-selector__footer-close"
                onClick={closeDropdown}
              >
                Закрыть
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
